import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
import clsx from "clsx";
import { useSectionForm } from "../../hooks/useSectionForm";
import { useFormStore } from "../store/formStore";
import { useToastWrapper } from "../../hooks/useToastWrapper";
import { useShouldValidate } from "../../hooks/useShouldValidate";
import { useEmailStore } from "../store/emailStore";
import Button from "../base/Button";
import ClickableText from "../base/ClickableText";
import SectionTitle from "../base/CardTitle";
import Card from "../base/Card";
import Spinner from "../base/Spinner";
import Divider from "../base/Divider";
import EmailForm from "../email-form/EmailForm";
import { additionalEmailsSchema, } from "../../schemas/schemas";
import { ExternalIcon } from "../Icons/Icons";
import EmailRowWrapper from "../email-row-wrapper/EmailRowWrapper";
import EmailRow from "../email-row/EmailRow";
import { FederateTokenProviderType } from "../../constants/profile";
const EMAIL_TYPE_NONE = "None";
const EmailsSection = ({ adapter, profile }) => {
    const hasBuilderProfile = profile?.additionalEmails?.find((item) => item?.tag?.isUsernameEmail === true);
    const { control, handleSubmit, isDirty, reset, setValue, formState: { errors }, setFocus, isSubmitting, } = useSectionForm(additionalEmailsSchema, "emails", {
        emails: profile?.additionalEmails || [],
    });
    const isFederateLinkedProfile = profile?.tokenProvider?.type === FederateTokenProviderType;
    const [isEditing, setIsEditing] = useState(false);
    const saveButtonRef = useRef(null);
    const { t } = useLocalizedString();
    const { emails, setInitialEmails, initialEmails, updateEmailStatus, updateInitialEmails, resetToInitialEmails, } = useEmailStore();
    const { handleWithToast } = useToastWrapper({
        loadingMessage: "Updating profile...",
        successMessage: "Profile updated successfully!",
        errorMessage: "Failed to update profile",
    });
    const shouldValidate = useShouldValidate(control, isDirty);
    const { editingSection, setEditingSection, hasChanges, setHasChanges, setBlockingModalOpen, setPendingSection, setResetFunction, shouldSave, setShouldSave, pendingSection, } = useFormStore();
    useEffect(() => {
        if (emails?.length) {
            setValue("emails", emails);
        }
    }, [emails, setValue]);
    useEffect(() => {
        setInitialEmails(profile?.additionalEmails);
    }, [profile?.additionalEmails, setInitialEmails]);
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const firstError = Object.keys(errors)[0];
            setFocus(firstError);
        }
    }, [errors, setFocus]);
    useEffect(() => {
        if (isDirty && isEditing) {
            setHasChanges(true);
        }
    }, [isDirty, isEditing, setHasChanges]);
    useEffect(() => {
        if (editingSection !== "emails") {
            setIsEditing(false);
        }
        if (editingSection === "emails") {
            setIsEditing(true);
        }
    }, [editingSection]);
    useEffect(() => {
        if (isEditing) {
            setResetFunction(() => reset);
        }
    }, [isEditing, reset, setResetFunction]);
    const onSubmit = useCallback(async (data) => {
        const emails = data.emails
            .filter((item) => item.type !== EMAIL_TYPE_NONE && item.email)
            .map(async (item) => {
            if (item.email) {
                const result = await adapter.UpdateEmail(item?.email, item.type);
                return result;
            }
            return;
        });
        await Promise.all(emails);
        updateInitialEmails();
        setIsEditing(false);
        setEditingSection(pendingSection ? pendingSection : null);
        setHasChanges(false);
    }, [adapter, profile.userId, setEditingSection, setHasChanges, pendingSection]);
    const updateProfileWithToast = handleWithToast(onSubmit);
    useEffect(() => {
        if (shouldSave === "emails" && isEditing && saveButtonRef.current) {
            saveButtonRef.current.click();
            setShouldSave(null);
        }
    }, [shouldSave, isEditing, setShouldSave]);
    const handleCancel = () => {
        reset({ emails: initialEmails });
        resetToInitialEmails();
        setIsEditing(false);
        setEditingSection(null);
        setHasChanges(false);
    };
    const handleEditClick = () => {
        if (hasChanges && editingSection !== "emails") {
            setBlockingModalOpen(true);
            setPendingSection("emails");
            return;
        }
        setIsEditing(true);
        setEditingSection("emails");
    };
    const handleFormSubmit = async () => {
        if (shouldValidate) {
            await handleSubmit(updateProfileWithToast);
        }
        else {
            handleCancel();
        }
    };
    const handleRedirect = () => {
        window.open("https://profile.aws.amazon.com/", "_blank");
    };
    const getButtonOptions = () => [
        {
            key: (_jsxs("span", { children: ["Manage my AWS Builder ID", " ", _jsx("span", { className: "inline", children: _jsx(ExternalIcon, { className: "inline" }) })] })),
            value: "manage",
            onClick: () => handleRedirect(),
        },
    ];
    const handleTypeUpdate = (email, newType) => {
        updateEmailStatus(email, { type: newType });
    };
    const isAdditionalEmailsEmpty = (emails) => {
        if (emails.length === 0)
            return true;
        const usernameEmails = emails.filter((email) => email?.tag?.isUsernameEmail);
        if (usernameEmails.length === 0)
            return true;
        return (emails.length === usernameEmails.length && usernameEmails.length === 1);
    };
    return (_jsx(Card, { children: _jsxs("div", { className: clsx("flex flex-col gap-4"), children: [_jsx("div", { className: "flex flex-col sm:flex-row gap-4 sm:items-center", children: _jsx(SectionTitle, { title: t("Emails") }) }), _jsxs("div", { className: "grid grid-cols-1", children: [_jsxs("div", { className: "grid grid-cols-1 gap-y-4", children: [_jsx("h3", { className: "text-[2rem] font-medium leading-normal", children: t(isFederateLinkedProfile
                                        ? "Federate email"
                                        : "AWS Builder ID email") }), _jsx("p", { className: "text-base", children: t("Your email address that you use to sign in and access different AWS experiences.") })] }), _jsx("div", { className: "mt-8 mb-8", "data-testid": "profileEmail", children: hasBuilderProfile ? (emails.map((field, index) => {
                                return field?.tag?.isUsernameEmail ? (_jsx(EmailRowWrapper, { email: field, isVerifying: false, onEmailUpdate: () => { }, onTypeUpdate: (value) => handleTypeUpdate(field.email, value), buttonOptions: getButtonOptions(), control: control, name: `emails.${index}`, isEditing: isEditing, className: "items-center" }, index)) : null;
                            })) : (_jsx(EmailRow, { isEditing: isEditing, email: profile?.email || "", type: "", buttonOptions: getButtonOptions(), onTypeUpdate: () => { }, className: "items-center", hideDropdown: true, onEmailUpdate: () => { } })) }), _jsx(Divider, {})] }), _jsxs("div", { className: "grid grid-cols-1", children: [_jsxs("div", { className: "grid grid-cols-1 gap-y-4", children: [_jsx("h3", { className: "text-[2rem] font-medium leading-normal", children: t("Additional emails") }), _jsx("p", { className: "text-base", children: t("Additional emails let you use different email addresses for select AWS experiences, while keeping your AWS Builder ID email for sign-in.") })] }), _jsxs("div", { className: "mt-8", children: [_jsx(EmailForm, { adapter: adapter, isEditing: isEditing, control: control, errors: errors, isDirty: isDirty, isSubmitting: isSubmitting, profile: profile }), isAdditionalEmailsEmpty(emails) && !isEditing && (_jsx("div", { className: "w-full flex items-center justify-center", children: _jsx("span", { className: "text-sm text-idk-disabled-text", children: t("No additional emails added.") }) }))] })] }), _jsx("div", { className: "w-full flex justify-end items-center mt-8", children: !isEditing ? (_jsx(Button, { "data-testid": "editButton", variant: "secondary", onClick: handleEditClick, children: t("Edit") })) : (_jsx("div", { className: "w-full flex flex-wrap items-center gap-4", children: _jsxs("div", { className: "flex items-center gap-8 ml-auto", children: [!isSubmitting && (_jsx(ClickableText, { "data-testid": "cancelButton", onClick: handleCancel, children: t("Cancel") })), _jsxs(Button, { "data-testid": "saveButton", ref: saveButtonRef, variant: "primary", onClick: handleFormSubmit, disabled: isSubmitting, children: [t("Save"), " ", isSubmitting && _jsx(Spinner, {})] })] }) })) })] }) }));
};
export default EmailsSection;
//# sourceMappingURL=EmailsSection.js.map